import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Camera, Zap, Loader2, ImagePlus, AlertTriangle, Link2 } from 'lucide-react';
import { useEmpPlanilhaVersoes, useEmpPlanilhaDetalhes } from '@/hooks/useEmpPlanilha';
import { useEmpExecucaoMutations, type ExecucaoInsert } from '@/hooks/useEmpExecucao';
import { useAuth } from '@/contexts/AuthContext';
import { deriveVirtualFront, isVirtualFrontId, resolveFrenteIdForInsert } from '@/lib/demoGuard';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUxAnalyticsFlag } from '@/hooks/useUxAnalyticsFlag';
import {
  resolveAnchor,
  resolveDuplicateCheckColumn,
  type ExecucaoMode,
} from '@/lib/execucao/anchorResolver';

interface QuickCaptureItem {
  id: string;
  codigo: string;
  descricao: string;
  unidade: string;
}

interface QuickCaptureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contratoId: string;
  /** Modo de âncora (PPU default — preserva contratos PPU). */
  mode?: ExecucaoMode;
  /** Contexto de OS (obrigatório quando mode='ubs'). */
  osContext?: { osId: string; numeroOs?: number | string | null };
  /** Fonte de itens externa — quando ausente, modo PPU lê planilha vigente. */
  itensSource?: QuickCaptureItem[];
}

export const QuickCaptureModal: React.FC<QuickCaptureModalProps> = ({
  open,
  onOpenChange,
  contratoId,
  mode = 'ppu',
  osContext,
  itensSource,
}) => {
  const { user, isDemo } = useAuth();
  const { toast } = useToast();
  const { logUxEvent } = useUxAnalyticsFlag();
  const { createExecucao, uploadEvidencia } = useEmpExecucaoMutations(contratoId);

  // Fonte PPU default: planilha vigente (compat retro). Outros modos: itensSource obrigatório.
  const { data: versoes = [] } = useEmpPlanilhaVersoes(contratoId);
  const versaoVigente = versoes.find((v) => v.status === 'vigente');
  const planilhaData = useEmpPlanilhaDetalhes(
    mode === 'ppu' && !itensSource ? versaoVigente?.id ?? null : null,
  );

  const itens: QuickCaptureItem[] = useMemo(() => {
    if (itensSource) return itensSource;
    if (mode === 'ppu') {
      return planilhaData.itens.map((i: any) => ({
        id: i.id,
        codigo: i.codigo_item,
        descricao: i.descricao,
        unidade: i.unidade,
      }));
    }
    return [];
  }, [itensSource, mode, planilhaData.itens]);

  // Frentes
  const { data: frentes = [] } = useQuery({
    queryKey: ['emp-frentes-quick', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_frentes_trabalho')
        .select('id, nome')
        .eq('contrato_id', contratoId)
        .eq('status', 'ativa')
        .order('nome');
      if (error) throw error;
      return data as { id: string; nome: string }[];
    },
    enabled: !!contratoId && open,
  });

  // Demo: força frente virtual runtime-only. O backend permite frente nula
  // apenas para profiles.is_demo=true; usuários reais seguem obrigados à frente real.
  const frentesEffective = useMemo(() => {
    if (isDemo) {
      const vf = deriveVirtualFront(contratoId);
      return [{ id: vf.id, nome: vf.nome }];
    }
    return frentes;
  }, [frentes, isDemo, contratoId]);


  const { data: ultimaFrente } = useQuery({
    queryKey: ['emp-ultima-frente', contratoId, user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from('emp_validacoes_execucao')
        .select('frente_id')
        .eq('contrato_id', contratoId)
        .eq('registrado_por', user.id)
        .not('frente_id', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data?.frente_id ?? null;
    },
    enabled: !!contratoId && !!user && open,
  });

  const [itemId, setItemId] = useState('');
  const [quantidade, setQuantidade] = useState('');
  const [frenteId, setFrenteId] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [duplicateAlertOpen, setDuplicateAlertOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!frenteId && frentesEffective.length > 0) {
      if (ultimaFrente && frentesEffective.some((f) => f.id === ultimaFrente)) {
        setFrenteId(ultimaFrente);
      } else if (frentesEffective.length === 1 && isVirtualFrontId(frentesEffective[0].id)) {
        // Demo fallback: pré-seleciona a frente virtual única.
        setFrenteId(frentesEffective[0].id);
      }
    }
  }, [ultimaFrente, frentesEffective, frenteId]);

  React.useEffect(() => {
    if (open) {
      setItemId('');
      setQuantidade('');
      const fallback =
        frentesEffective.length === 1 && isVirtualFrontId(frentesEffective[0].id)
          ? frentesEffective[0].id
          : '';
      setFrenteId(
        ultimaFrente && frentesEffective.some((f) => f.id === ultimaFrente)
          ? ultimaFrente
          : fallback,
      );
      setObservacoes('');
      setFile(null);
      setSubmitting(false);
    }
  }, [open]);

  const selectedItem = useMemo(() => itens.find((i) => i.id === itemId), [itens, itemId]);
  const canSubmit = !!itemId && !!frenteId && !!quantidade && parseFloat(quantidade) > 0 && !submitting;

  const checkDuplicateAndSubmit = useCallback(async () => {
    if (!canSubmit || !user) return;
    try {
      const dupCol = resolveDuplicateCheckColumn(mode);
      const dataExec = new Date().toISOString().split('T')[0];
      // Duplicate-check: item (anchor) + data_execucao + frente_id. Quantidade NÃO entra
      // (corrige falso-negativo: dois apontamentos com qtds diferentes no mesmo item/dia/frente
      // são duplicata operacional).
      const dbFrenteId = resolveFrenteIdForInsert(frenteId);
      let q = supabase
        .from('emp_validacoes_execucao')
        .select('id')
        .eq('contrato_id', contratoId)
        .eq(dupCol, itemId)
        .eq('data_execucao', dataExec)
        .limit(1);
      q = dbFrenteId === null ? q.is('frente_id', null) : q.eq('frente_id', dbFrenteId);
      const { data: recent } = await q;
      if (recent && recent.length > 0) {
        setDuplicateAlertOpen(true);
        return;
      }
    } catch {
      /* fail-open */
    }
    await doSubmit();
  }, [canSubmit, user, contratoId, itemId, quantidade, mode, frenteId]);

  const doSubmit = async () => {
    if (!canSubmit || !user || !selectedItem) return;
    // BLOCO 5 — defesa fail-fast: UBS sem osContext é estado inválido.
    if (mode === 'ubs' && !osContext?.osId) {
      throw new Error('[QuickCaptureModal] Execução UBS sem contexto de OS');
    }
    setSubmitting(true);
    try {
      const anchor = resolveAnchor(mode, itemId);

      logUxEvent({
        contrato_id: contratoId,
        tipo_evento: 'quick_capture_submit',
        payload: {
          mode,
          os_id: osContext?.osId ?? null,
          quantidade: parseFloat(quantidade),
          frente_id: frenteId,
          com_evidencia: !!file,
          ...anchor,
        },
      });

      // Monta payload via union — exatamente 1 anchor.
      const base = {
        contrato_id: contratoId,
        frente_id: resolveFrenteIdForInsert(frenteId) as unknown as string,
        data_execucao: new Date().toISOString().split('T')[0],
        quantidade_executada: parseFloat(quantidade),
        unidade: selectedItem.unidade,
        tipo: 'execucao' as const,
        observacoes: observacoes.trim() || null,
      };
      let payload: ExecucaoInsert;
      if (mode === 'ubs') {
        if (!osContext?.osId) throw new Error('Modo UBS exige osContext.osId.');
        payload = { ...base, ...anchor, os_id: osContext.osId } as ExecucaoInsert;
      } else {
        payload = { ...base, ...anchor } as ExecucaoInsert;
      }

      const execResult = await createExecucao.mutateAsync(payload);

      if (execResult?.id) {
        logUxEvent({
          contrato_id: contratoId,
          tipo_evento: 'execucao_registrada_quick_capture',
          entidade: 'execucao',
          entidade_id: execResult.id,
          payload: { mode, os_id: osContext?.osId ?? null, quantidade: parseFloat(quantidade), frente_id: frenteId, com_evidencia: !!file },
        });
        if (file) {
          await uploadEvidencia.mutateAsync({
            execucaoId: execResult.id,
            file,
            descricao: 'Quick Capture',
            categoria: 'execucao',
          });
        }
      }

      toast({
        title: 'Execução registrada',
        description: `Seguirá para Qualidade → Planejamento → Medição${!file ? ' · Sem evidência anexada' : ''}`,
      });
      onOpenChange(false);
    } catch {
      /* mutation toasts */
    } finally {
      setSubmitting(false);
    }
  };

  const itemLabel =
    mode === 'eap' ? 'Atividade do cronograma' :
    mode === 'ubs' ? 'Item da OS' :
    'Item contratual';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              Registro Rápido
              {osContext && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-muted-foreground">
                  <Link2 className="h-3.5 w-3.5" />
                  OS{osContext.numeroOs != null ? `-${String(osContext.numeroOs).padStart(3, '0')}` : ''}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{itemLabel} <span className="text-destructive">*</span></Label>
              <Select value={itemId} onValueChange={setItemId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar item..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {itens.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.codigo} — {item.descricao} ({item.unidade})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {itens.length === 0 && (
                <p className="text-xs text-muted-foreground">Nenhum item disponível para este modo.</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>
                Quantidade <span className="text-destructive">*</span>
                {selectedItem && <span className="text-muted-foreground ml-1">({selectedItem.unidade})</span>}
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0,00"
                value={quantidade}
                onChange={(e) => setQuantidade(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Frente de trabalho <span className="text-destructive">*</span></Label>
              <Select value={frenteId} onValueChange={setFrenteId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar frente..." />
                </SelectTrigger>
                <SelectContent>
                  {frentesEffective.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.nome}{isVirtualFrontId(f.id) ? ' (simulada)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Camera className="h-4 w-4" />
                Foto
                <span className="text-muted-foreground text-xs ml-1">(opcional)</span>
              </Label>
              <div
                className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {file ? (
                  <div className="flex items-center gap-2 justify-center text-sm">
                    <ImagePlus className="h-4 w-4 text-primary" />
                    <span className="truncate max-w-[200px]">{file.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={(e) => { e.stopPropagation(); setFile(null); }}
                    >
                      Remover
                    </Button>
                  </div>
                ) : (
                  <div className="text-muted-foreground text-sm">
                    <ImagePlus className="h-6 w-6 mx-auto mb-1 opacity-50" />
                    <p>Adicionar foto aumenta a rastreabilidade</p>
                    <p className="text-xs">(recomendado)</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setFile(f);
                  e.target.value = '';
                }}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Observação <span className="text-muted-foreground text-xs">(opcional)</span></Label>
              <Textarea
                placeholder="Detalhes adicionais..."
                rows={2}
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button onClick={checkDuplicateAndSubmit} disabled={!canSubmit}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Registrando...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-1" />
                  Registrar execução
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={duplicateAlertOpen} onOpenChange={setDuplicateAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Registro semelhante recente
            </AlertDialogTitle>
            <AlertDialogDescription>
              Um registro com o mesmo item e quantidade foi feito nos últimos 2 minutos. Deseja continuar mesmo assim?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setDuplicateAlertOpen(false); doSubmit(); }}>
              Registrar mesmo assim
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
